import React, { useState, useEffect, useRef, useContext } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AppContext from 'context/Context';
import {
    faTag,
    faPen,
    faCheck,
    faTimes,
    faSpinner,
    faUser,
    faSignOutAlt,
    faArrowLeft,
    faChevronDown,
    faBars,
    faHeadset,
    faSun,
    faMoon
} from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import { getToken, deleteToken } from 'firebase/messaging';
import { messaging } from '../../../firebase/firebaseConfig';

const getAvatarSrc = img => {
    if (!img || typeof img !== 'string') return '/img/team/nofoto50.png';
    if (img.startsWith('blob:') || img.startsWith('http') || img.startsWith('/')) return img;
    if (img.length > 100) return img;
    return '/img/team/nofoto50.png';
};

const formatTitleCase = str => {
    if (!str) return '';
    return str
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const getDisplayName = (fullName, fallbackUser) => {
    if (!fullName) return fallbackUser || 'Usuario';
    const clean = fullName.trim();
    if (!clean) return fallbackUser || 'Usuario';

    const parts = clean.split(/\s+/).filter(Boolean);
    const toTitleCase = word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

    if (parts.length === 1) {
        return toTitleCase(parts[0]);
    }
    const firstName = toTitleCase(parts[0]);
    const surname = parts.length >= 3 ? toTitleCase(parts[2]) : toTitleCase(parts[1]);
    return `${firstName} ${surname}`;
};

const SupportEngineNavbar = ({
    activeTab,
    settingsSubTab,
    selectedTicket,
    onUpdateTicket,
    onToggleMobileMenu
}) => {
    const { config: { isDark } = {}, setConfig } = useContext(AppContext);
    const { token, setToken, nombreCompleto, profileImage, email, idUsuario, usuario } = useToken();
    const { showSuccessToast, showErrorToast } = useToast();

    const displayName = getDisplayName(nombreCompleto, usuario);
    const fullDisplayName = formatTitleCase(nombreCompleto) || usuario || 'Usuario';

    const [isEditingMotivo, setIsEditingMotivo] = useState(false);
    const [motivoText, setMotivoText] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const inputRef = useRef(null);

    const ticketId = selectedTicket?.idTicket || selectedTicket?.id;
    const motivoActual = selectedTicket?.vAsunto || selectedTicket?.subject || '';

    // Mantener sincronizado el texto del motivo cuando cambia el ticket seleccionado
    useEffect(() => {
        if (!isEditingMotivo) {
            setMotivoText(motivoActual);
        }
    }, [motivoActual, ticketId, isEditingMotivo]);

    // Enfocar y seleccionar el texto al activar la edición
    useEffect(() => {
        if (isEditingMotivo && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditingMotivo]);

    const handleStartEdit = () => {
        setMotivoText(motivoActual);
        setIsEditingMotivo(true);
    };

    const handleCancelEdit = () => {
        setMotivoText(motivoActual);
        setIsEditingMotivo(false);
    };

    const handleSaveMotivo = async e => {
        if (e) e.preventDefault();
        if (!ticketId || isSaving) return;

        const trimmed = motivoText.trim();
        if (trimmed === motivoActual) {
            setIsEditingMotivo(false);
            return;
        }

        setIsSaving(true);
        try {
            const response = await fetch('/api/SupportEngine/UpdateTicketSubject', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    Authorization: 'Bearer ' + token
                },
                body: JSON.stringify({
                    idTicket: parseInt(ticketId, 10),
                    subject: trimmed
                })
            });

            if (response.ok) {
                if (onUpdateTicket) {
                    onUpdateTicket({ vAsunto: trimmed });
                }
                setIsEditingMotivo(false);
                if (showSuccessToast) {
                    showSuccessToast('Motivo actualizado', 'El motivo del ticket se guardó correctamente.');
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                if (showErrorToast) {
                    showErrorToast('Error', errorData.message || 'No se pudo actualizar el motivo.');
                }
            }
        } catch (err) {
            if (showErrorToast) {
                showErrorToast('Error de conexión', err.message || 'Ocurrió un error al guardar.');
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleCerrarSesion = async () => {
        let tokenFirebase = '';
        try {
            tokenFirebase = await getToken(messaging, {
                vapidKey: 'BDgxI0yryiAvMlu9vmMDZibB-bmaSt5pj1Vrun_IsGWmzS3h5ubI69nRxId9HteusWK3d1cA1_Y1tFMdV4UjUkE'
            });
        } catch (err) {
            console.error('Error obteniendo token Firebase:', err);
        }

        try {
            await fetch('/api/account/CerrarSesion/', {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    idUsuario,
                    tokenFirebase
                })
            });
        } catch (err) {
            console.error('Error cerrando sesión en API:', err);
        }

        try {
            await deleteToken(messaging);
        } catch (err) {
            console.error('Error eliminando token Firebase:', err);
        }

        sessionStorage.clear();
        setToken(null);
    };

    const renderTitle = () => {
        if (selectedTicket) {
            return `Ticket #${ticketId}`;
        }
        if (activeTab === 'tickets') {
            return 'Centro de Operaciones';
        }
        if (activeTab === 'reports') {
            return 'Reportes de Tickets';
        }
        if (activeTab === 'dashboard') {
            return 'Visión General';
        }
        if (activeTab === 'settings') {
            if (settingsSubTab === 'atajos') return 'Configuración de Atajos';
            if (settingsSubTab === 'incidencias') return 'Configuración de Incidencias';
            if (settingsSubTab === 'horarios') return 'Configuración de Horarios';
            if (settingsSubTab === 'mensajes') return 'Configuración de Mensajes Automáticos';
            return 'Configuración General';
        }
        return activeTab.charAt(0).toUpperCase() + activeTab.slice(1);
    };

    const isSettingsTab = activeTab === 'settings' && !selectedTicket;

    return (
        <header className="se-header d-flex align-items-center justify-content-between">
            <style>{`
                @media (max-width: 575.98px) {
                    .se-navbar-title-settings {
                        font-size: 0.88rem !important;
                        line-height: 1.25 !important;
                        letter-spacing: -0.01em !important;
                    }
                }
                @media (min-width: 576px) and (max-width: 991.98px) {
                    .se-navbar-title-settings {
                        font-size: 1rem !important;
                    }
                }
            `}</style>

            {/* Title & Motivo */}
            <div className="d-flex align-items-center gap-2 min-w-0 overflow-hidden">
                {/* Botón hamburguesa visible solo en móvil */}
                <button
                    type="button"
                    className="btn btn-sm border d-lg-none d-inline-flex align-items-center justify-content-center me-1 flex-shrink-0"
                    onClick={onToggleMobileMenu}
                    style={{
                        width: '34px',
                        height: '34px',
                        borderRadius: '8px',
                        backgroundColor: isDark ? '#1e293b' : '#ffffff',
                        borderColor: isDark ? '#233142' : '#e2e8f0',
                        color: isDark ? '#94a3b8' : '#334155'
                    }}
                    aria-label="Abrir menú de navegación"
                >
                    <FontAwesomeIcon icon={faBars} />
                </button>

                {/* En móvil: Logo Headset al lado de la hamburguesa (matching mockup) */}
                <div className="d-flex d-lg-none align-items-center flex-shrink-0">
                    <div className="se-logo-box-sm">
                        <FontAwesomeIcon icon={faHeadset} />
                    </div>
                </div>

                {/* Título de la vista (adaptado para que encaje bien en móvil si es de configuración) */}
                <div className="se-navbar-title-container min-w-0">
                    <h4
                        className={`fw-bold mb-0 text-900 text-truncate se-navbar-title ${isSettingsTab ? 'se-navbar-title-settings' : ''}`}
                        style={{ letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}
                    >
                        {renderTitle()}
                    </h4>
                </div>

                {selectedTicket && (
                    <div className="d-none d-xl-flex align-items-center ms-1">
                        <span className="text-muted me-2" style={{ opacity: 0.4, fontSize: '1.2rem', fontWeight: 300 }}>·</span>

                        {isEditingMotivo ? (
                            <form onSubmit={handleSaveMotivo} className="d-flex align-items-center gap-1">
                                <input
                                    ref={inputRef}
                                    type="text"
                                    className="form-control form-control-sm se-motivo-input"
                                    placeholder="Escribe el motivo del ticket..."
                                    value={motivoText}
                                    onChange={e => setMotivoText(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Escape') {
                                            e.preventDefault();
                                            handleCancelEdit();
                                        }
                                    }}
                                    disabled={isSaving}
                                    maxLength={250}
                                    style={isDark ? { backgroundColor: '#1e293b', color: '#f8fafc', borderColor: '#3b82f6' } : {}}
                                />
                                <button
                                    type="submit"
                                    className="btn btn-sm btn-primary d-flex align-items-center justify-content-center p-0 rounded-circle"
                                    style={{ width: '28px', height: '28px', minWidth: '28px' }}
                                    title="Guardar motivo (Enter)"
                                    disabled={isSaving}
                                >
                                    {isSaving ? (
                                        <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '0.75rem' }} />
                                    ) : (
                                        <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.75rem' }} />
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    className="btn btn-sm d-flex align-items-center justify-content-center p-0 rounded-circle shadow-none"
                                    style={{
                                        width: '28px',
                                        height: '28px',
                                        minWidth: '28px',
                                        backgroundColor: isDark ? '#1e293b' : '#f8fafc',
                                        border: isDark ? '1px solid #233142' : '1px solid #e2e8f0'
                                    }}
                                    title="Cancelar (Esc)"
                                    disabled={isSaving}
                                >
                                    <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.75rem', color: isDark ? '#94a3b8' : '#64748b' }} />
                                </button>
                            </form>
                        ) : (
                            <div
                                className="se-motivo-badge d-flex align-items-center gap-2"
                                style={{
                                    backgroundColor: isDark ? '#1e293b' : '#f8fafc',
                                    borderColor: isDark ? '#233142' : '#e2e8f0'
                                }}
                                onClick={handleStartEdit}
                                title="Clic para editar el motivo del ticket"
                                role="button"
                                tabIndex={0}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleStartEdit();
                                    }
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faTag}
                                    style={{ fontSize: '0.72rem', color: motivoActual ? '#3b82f6' : (isDark ? '#64748b' : '#94a3b8') }}
                                />
                                <span
                                    className={motivoActual ? 'fw-medium' : 'fst-italic'}
                                    style={{
                                        fontSize: '0.86rem',
                                        maxWidth: '340px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        color: motivoActual ? (isDark ? '#e2e8f0' : '#475569') : (isDark ? '#94a3b8' : '#64748b')
                                    }}
                                >
                                    {motivoActual || '+ Agregar motivo'}
                                </span>
                                <span className="se-motivo-edit-icon ms-1" title="Editar motivo">
                                    <FontAwesomeIcon icon={faPen} style={{ fontSize: '0.68rem', color: isDark ? '#94a3b8' : '#64748b' }} />
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Right User & Notifications */}
            <div className="d-flex align-items-center gap-2 ms-auto">
                {/* Alternar Modo Oscuro / Claro directo (oculto en móvil) */}
                <button
                    type="button"
                    className="se-notif-btn d-none d-md-inline-flex"
                    title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
                    onClick={() => setConfig('isDark', !isDark)}
                    aria-label="Alternar tema de color"
                >
                    <FontAwesomeIcon
                        icon={isDark ? faSun : faMoon}
                        style={{ fontSize: '1.05rem', color: isDark ? '#fbbf24' : '#64748b' }}
                    />
                </button>

                {/* User Profile Dropdown */}
                <Dropdown align="end" className="d-inline-flex align-items-center m-0 p-0">
                    <Dropdown.Toggle
                        as="div"
                        bsPrefix="se-user-pill"
                        role="button"
                    >
                        <img
                            src={getAvatarSrc(profileImage)}
                            alt={displayName}
                            className="se-user-avatar"
                            onError={e => {
                                e.target.src = '/img/team/nofoto50.png';
                            }}
                        />
                        <span className="se-user-name d-none d-sm-inline-block">
                            {displayName}
                        </span>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className="se-user-chevron"
                        />
                    </Dropdown.Toggle>

                    <Dropdown.Menu
                        className="dropdown-menu-end shadow-sm border py-2"
                        style={{ minWidth: '240px', borderRadius: '12px' }}
                    >
                        <div className="px-3 py-2 border-bottom mb-1">
                            <div className="fw-bold text-900" style={{ fontSize: '0.90rem' }}>
                                {fullDisplayName}
                            </div>
                            <div
                                className="text-muted text-truncate"
                                style={{ fontSize: '0.75rem', maxWidth: '210px' }}
                                title={email}
                            >
                                {email || 'Sin correo asignado'}
                            </div>
                        </div>

                        <Dropdown.Item
                            as="button"
                            type="button"
                            onClick={() => setConfig('isDark', !isDark)}
                            className="d-flex align-items-center justify-content-between py-2"
                        >
                            <div className="d-flex align-items-center gap-2">
                                <FontAwesomeIcon
                                    icon={isDark ? faSun : faMoon}
                                    style={{ width: '14px', color: isDark ? '#fbbf24' : '#64748b' }}
                                />
                                <span style={{ fontSize: '0.84rem' }}>
                                    {isDark ? 'Modo Claro' : 'Modo Oscuro'}
                                </span>
                            </div>
                            <span
                                className={`badge ${isDark ? 'bg-primary' : 'bg-200 text-700'}`}
                                style={{ fontSize: '0.68rem' }}
                            >
                                {isDark ? 'Oscuro' : 'Claro'}
                            </span>
                        </Dropdown.Item>

                        <Dropdown.Item as={Link} to="/perfil" className="d-flex align-items-center gap-2 py-2">
                            <FontAwesomeIcon icon={faUser} style={{ width: '14px', color: '#64748b' }} />
                            <span style={{ fontSize: '0.84rem' }}>Configuración de Perfil</span>
                        </Dropdown.Item>

                        <Dropdown.Item as={Link} to="/" className="d-flex align-items-center gap-2 py-2">
                            <FontAwesomeIcon icon={faArrowLeft} style={{ width: '14px', color: '#64748b' }} />
                            <span style={{ fontSize: '0.84rem' }}>Volver al Admin Panel</span>
                        </Dropdown.Item>

                        <Dropdown.Divider className="my-1" />

                        <Dropdown.Item onClick={handleCerrarSesion} className="d-flex align-items-center gap-2 py-2 text-danger">
                            <FontAwesomeIcon icon={faSignOutAlt} style={{ width: '14px' }} />
                            <span style={{ fontSize: '0.84rem' }}>Salir del sistema</span>
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        </header>
    );
};

export default SupportEngineNavbar;